/**
 * Client-side checks for the portal access JWT (stored as gcpToken).
 * Does not verify the signature — JWT_SECRET stays on the server. This only rejects
 * malformed tokens, missing claims, and expiry so localStorage cannot impersonate a session
 * with arbitrary strings.
 */

const JWT_PARTS = 3;
/** Seconds of leeway for clock skew between client and token issuance. */
const EXP_LEEWAY_SEC = 60;

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split(".");
  if (parts.length !== JWT_PARTS) return null;
  const payloadSegment = parts[1];
  if (!payloadSegment) return null;
  try {
    const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    const padded = pad ? base64 + "=".repeat(4 - pad) : base64;
    const json = atob(padded);
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Returns true if the string looks like a non-expired portal JWT with a user id claim.
 */
export function isPortalSessionTokenValid(token: string | null | undefined): boolean {
  if (token == null || token === "" || token === "null") return false;

  const payload = decodeJwtPayload(token);
  if (!payload) return false;

  const userId = payload.userId;
  if (userId == null || String(userId).length === 0) return false;

  const exp = payload.exp;
  if (typeof exp !== "number" || !Number.isFinite(exp)) return false;

  const now = Math.floor(Date.now() / 1000);
  return now < exp + EXP_LEEWAY_SEC;
}
